<template>
    <div class="space-y-4">
        <!-- Cards -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <stat-card :loading="loading && !cards" name="Com falha na janela" :stat="cards?.failing ?? 0" />
            <stat-card :loading="loading && !cards" name="Falham 100%" :stat="cards?.always_failing ?? 0" />
            <stat-card
                :loading="loading && !cards"
                :name="`Sem sucesso há ${cards?.no_success_days ?? 180} dias`"
                :stat="cards?.no_success ?? 0"
            />
            <stat-card :loading="loading && !cards" name="Só 429" :stat="cards?.only_429 ?? 0" />
        </div>

        <p v-if="cards?.computed_at" class="text-xs text-gray-500 dark:text-gray-400">
            Resumo calculado em {{ formatDate(cards.computed_at) }} (janela de 24 h).
            <span v-if="cards.history_at">Histórico do Databricks em {{ formatDate(cards.history_at) }}.</span>
            <span v-else>Histórico do Databricks indisponível: último sucesso e gráfico limitados à janela.</span>
        </p>

        <!-- Filtros -->
        <form @submit.prevent="search" class="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <div>
                <label class="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Webhook id</label>
                <input
                    v-model="webhookIdSearch"
                    type="number"
                    min="1"
                    class="text-input"
                    placeholder="Abrir detalhe pelo id"
                    @keydown.enter.prevent="openDetailFromSearch"
                />
            </div>
            <div>
                <label class="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Status</label>
                <select v-model="filters.status" class="select-input">
                    <option value="">Todos</option>
                    <option value="enabled">enabled</option>
                    <option value="disabled">disabled</option>
                    <option value="deactivated">deactivated</option>
                    <option value="none">sem status</option>
                </select>
            </div>
            <div>
                <label class="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Produtor</label>
                <users-select v-model="producer" :multiple="false" />
            </div>
            <div>
                <label class="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Taxa mínima (%)</label>
                <input v-model="filters.min_rate" type="number" min="0" max="100" class="text-input" placeholder="Ex.: 50" />
            </div>
            <div>
                <label class="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Sem sucesso há mais de (dias)</label>
                <input v-model="filters.no_success_days" type="number" min="1" class="text-input" placeholder="Ex.: 30" />
            </div>
            <div class="flex items-end gap-3">
                <label class="flex items-center gap-2 pb-3 text-sm text-gray-700 dark:text-white">
                    <input
                        v-model="filters.only_429"
                        type="checkbox"
                        class="h-4 w-4 rounded border-black/20 bg-transparent text-indigo-500 focus:ring-indigo-500/40 dark:border-white/25"
                    />
                    Só 429
                </label>
                <submit-button :loading="loading" size="sm" class="mb-1">Filtrar</submit-button>
            </div>
        </form>

        <!-- Detalhe -->
        <div
            v-if="detailId"
            class="rounded-xl border border-black/5 bg-white p-4 shadow-xs dark:border-white/10 dark:bg-white/[0.02]"
        >
            <div class="mb-3 flex items-center">
                <h2 class="text-base font-semibold text-gray-900 dark:text-white">Webhook {{ detailId }}</h2>
                <button class="ml-auto text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white" @click.prevent="closeDetail">
                    Fechar
                </button>
            </div>

            <ticto-loading v-if="loadingDetail" />

            <div v-else-if="detail" class="space-y-6">
                <!-- Cabecalho -->
                <dl class="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                    <div class="lg:col-span-3">
                        <dt class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">URL</dt>
                        <dd class="truncate text-gray-800 dark:text-gray-100" v-tooltip="detail.webhook.url">
                            <template v-if="detail.webhook.exists">{{ detail.webhook.url }}</template>
                            <span v-else class="text-amber-600 dark:text-amber-400">webhook não encontrado na tabela webhooks (só há logs no DynamoDB)</span>
                        </dd>
                    </div>
                    <div>
                        <dt class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Produtor</dt>
                        <dd class="text-gray-800 dark:text-gray-100">
                            <template v-if="detail.webhook.user">{{ detail.webhook.user.name }} - {{ detail.webhook.user.email }}</template>
                            <template v-else>-</template>
                        </dd>
                    </div>
                    <div>
                        <dt class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Status</dt>
                        <dd><span :class="statusBadgeClass(detail.webhook.status)">{{ detail.webhook.status || 'sem status' }}</span></dd>
                    </div>
                    <div>
                        <dt class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Criado em</dt>
                        <dd class="text-gray-800 dark:text-gray-100">{{ formatDate(detail.webhook.created_at) }}</dd>
                    </div>
                    <div>
                        <dt class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Último sucesso</dt>
                        <dd class="text-gray-800 dark:text-gray-100">{{ detail.summary?.last_success_at ? formatDate(detail.summary.last_success_at) : 'nunca' }}</dd>
                    </div>
                    <div>
                        <dt class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Falhando desde</dt>
                        <dd class="text-gray-800 dark:text-gray-100">{{ detail.summary?.first_failure_at ? formatDate(detail.summary.first_failure_at) : '-' }}</dd>
                    </div>
                    <div>
                        <dt class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Janela (24 h)</dt>
                        <dd class="text-gray-800 dark:text-gray-100">
                            <template v-if="detail.summary">
                                {{ detail.summary.failures }} falhas em {{ detail.summary.attempts }} tentativas ({{ percent(detail.summary.failure_rate) }})
                            </template>
                            <template v-else>sem tentativas na janela</template>
                        </dd>
                    </div>
                </dl>

                <!-- Quebra por evento -->
                <div>
                    <h3 class="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Por evento (janela de 24 h)</h3>
                    <inline-empty-state v-if="detail.events.length === 0" :icon="emptyIcon" title="Sem tentativas na janela" />
                    <div v-else class="overflow-x-auto rounded-xl border border-black/5 dark:border-white/10">
                        <table class="w-full table-auto border-collapse text-sm">
                            <thead>
                                <tr class="border-b border-black/5 dark:border-white/10">
                                    <th v-for="header in ['Evento', 'Tentativas', 'Falhas', 'Taxa', 'Código dominante', 'Último sucesso', 'Falhando desde']" :key="header" class="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                                        {{ header }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="event in detail.events" :key="event.event">
                                    <td class="table-cell font-mono text-xs">{{ event.event }}</td>
                                    <td class="table-cell">{{ event.attempts }}</td>
                                    <td class="table-cell">{{ event.failures }}</td>
                                    <td class="table-cell"><span :class="rateClass(event.failure_rate)">{{ percent(event.failure_rate) }}</span></td>
                                    <td class="table-cell">{{ codeLabel(event.dominant_code, event.dominant_code_count) }}</td>
                                    <td class="table-cell">{{ event.last_success_at ? formatDate(event.last_success_at) : 'nunca' }}</td>
                                    <td class="table-cell">{{ event.first_failure_at ? formatDate(event.first_failure_at) : '-' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Grafico -->
                <div>
                    <h3 class="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Taxa de falha por dia (últimos {{ detail.daily.length }} dias)</h3>
                    <inline-empty-state v-if="!hasChartData" :icon="emptyIcon" title="Sem tentativas no período" />
                    <highcharts v-else :key="dark ? 'dark' : 'light'" class="hc" :options="themedChartObject"></highcharts>
                </div>

                <!-- Ultimas tentativas -->
                <div>
                    <h3 class="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Últimas {{ detail.recent_attempts.length }} tentativas</h3>
                    <inline-empty-state v-if="detail.recent_attempts.length === 0" :icon="emptyIcon" title="Sem tentativas registradas" />
                    <div v-else class="overflow-x-auto rounded-xl border border-black/5 dark:border-white/10">
                        <table class="w-full table-auto border-collapse text-sm">
                            <thead>
                                <tr class="border-b border-black/5 dark:border-white/10">
                                    <th v-for="header in ['Data', 'Evento', 'Status', 'Código']" :key="header" class="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                                        {{ header }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(attempt, index) in detail.recent_attempts" :key="index">
                                    <td class="table-cell">{{ formatDate(attempt.created_at) }}</td>
                                    <td class="table-cell font-mono text-xs">{{ attempt.event || '-' }}</td>
                                    <td class="table-cell"><span :class="attemptStatusClass(attempt.status)">{{ attempt.status || '-' }}</span></td>
                                    <td class="table-cell">{{ attempt.status_code ?? 'sem resposta' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Ranking -->
        <table-component
            :loading="loading"
            :headers="['Webhook', 'Produtor', 'Status', 'Tentativas', 'Falhas', 'Taxa', 'Código dominante', 'Último sucesso', 'Eventos']"
            :pagination="pagination"
            :is-empty="!loading && webhooks.length === 0"
            @fetch="fetch"
        >
            <template v-slot:tableHeader>
                <div class="mb-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                    Ranking por taxa de falha na janela
                    <span v-if="pagination.total" class="ml-auto">
                        Exibindo {{ pagination.from }} a {{ pagination.to }} de {{ pagination.total }}
                    </span>
                </div>
            </template>
            <tr
                v-for="webhook in webhooks"
                :key="webhook.webhook_id"
                class="cursor-pointer"
                :class="{ 'bg-indigo-50 dark:bg-indigo-500/10': detailId === webhook.webhook_id }"
                @click="openDetail(webhook.webhook_id)"
            >
                <td class="table-cell">
                    <div class="font-medium">{{ webhook.webhook_id }}</div>
                    <div class="max-w-[220px] truncate text-xs text-gray-500 dark:text-gray-400" v-tooltip="webhook.url">{{ webhook.url || '-' }}</div>
                </td>
                <td class="table-cell">
                    <template v-if="webhook.user">
                        <div>{{ webhook.user.name }}</div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">{{ webhook.user.email }}</div>
                    </template>
                    <span v-else>-</span>
                </td>
                <td class="table-cell"><span :class="statusBadgeClass(webhook.status)">{{ webhook.status || 'sem status' }}</span></td>
                <td class="table-cell">{{ webhook.attempts }}</td>
                <td class="table-cell">{{ webhook.failures }}</td>
                <td class="table-cell"><span :class="rateClass(webhook.failure_rate)">{{ percent(webhook.failure_rate) }}</span></td>
                <td class="table-cell whitespace-nowrap">{{ codesLabel(webhook.failure_codes) }}</td>
                <td class="table-cell whitespace-nowrap">
                    <template v-if="webhook.last_success_at">
                        {{ formatDate(webhook.last_success_at) }}
                        <div class="text-xs text-gray-500 dark:text-gray-400">há {{ webhook.days_without_success }} dias</div>
                    </template>
                    <template v-else>
                        nunca
                        <div v-if="webhook.days_without_success !== null" class="text-xs text-gray-500 dark:text-gray-400">
                            webhook com {{ webhook.days_without_success }} dias
                        </div>
                    </template>
                </td>
                <td class="table-cell">
                    <div class="flex flex-wrap gap-1">
                        <span v-for="event in webhook.failing_events" :key="`f-${event}`" class="rounded bg-red-100 px-1.5 py-0.5 font-mono text-[11px] text-red-700 dark:bg-red-500/20 dark:text-red-300">{{ event }}</span>
                        <span v-for="event in webhook.working_events" :key="`w-${event}`" class="rounded bg-green-100 px-1.5 py-0.5 font-mono text-[11px] text-green-700 dark:bg-green-500/20 dark:text-green-300">{{ event }}</span>
                    </div>
                </td>
            </tr>
        </table-component>
    </div>
</template>

<script>
import { ChartBarIcon } from "@heroicons/vue/24/outline";
import { mapWritableState } from "pinia";
import { useToast } from "vue-toastification";
import { Store } from "../../stores/store";
import SubmitButton from "../../components/forms/SubmitButton.vue";
import UsersSelect from "../../components/forms/UsersSelect.vue";
import InlineEmptyState from "../../components/layouts/InlineEmptyState.vue";
import StatCard from "../../components/layouts/StatCard.vue";
import TictoLoading from "../../components/layouts/TictoLoading.vue";
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import HelperMixin from "../../mixins/HelperMixin.vue";

export default {
    components: { SubmitButton, UsersSelect, InlineEmptyState, StatCard, TictoLoading, TableComponent },
    mixins: [HelperMixin],

    setup() {
        return { toast: useToast() };
    },

    data() {
        return {
            loading: false,
            loadingDetail: false,
            cards: null,
            webhooks: [],
            pagination: { current_page: 1, per_page: 25, total: 0, from: 0, to: 0, last_page: 1 },
            filters: { status: "", min_rate: "", no_success_days: "", only_429: false },
            producer: null,
            webhookIdSearch: "",
            detailId: null,
            detail: null,
        };
    },

    computed: {
        ...mapWritableState(Store, ["dark"]),

        emptyIcon() {
            return ChartBarIcon;
        },

        hasChartData() {
            return (this.detail?.daily ?? []).some((day) => day.attempts > 0);
        },

        // Mesmo padrao de CommissionsChart.vue: o tema global do Highcharts nao
        // acompanha o toggle claro/escuro do app, entao as cores entram aqui.
        themedChartObject() {
            const gridColor = this.dark ? "#374151" : "#e5e7eb";
            const textColor = this.dark ? "#9ca3af" : "#6b7280";
            const daily = this.detail?.daily ?? [];

            return {
                chart: { backgroundColor: "transparent", style: { fontFamily: "inherit" }, height: 260, alignTicks: false },
                credits: { enabled: false },
                title: { text: null },
                legend: { enabled: true, itemStyle: { color: this.dark ? "#e5e7eb" : "#374151" } },
                xAxis: {
                    type: "datetime",
                    gridLineColor: gridColor,
                    lineColor: gridColor,
                    tickColor: gridColor,
                    labels: { format: "{value:%d/%m}", style: { color: textColor } },
                },
                yAxis: [
                    {
                        title: { text: "Taxa de falha", style: { color: textColor } },
                        min: 0,
                        max: 100,
                        endOnTick: false,
                        tickInterval: 25,
                        gridLineColor: gridColor,
                        labels: { format: "{value}%", style: { color: textColor } },
                    },
                    {
                        title: { text: "Tentativas", style: { color: textColor } },
                        min: 0,
                        opposite: true,
                        gridLineWidth: 0,
                        labels: { style: { color: textColor } },
                    },
                ],
                tooltip: {
                    shared: true,
                    xDateFormat: "%d/%m/%Y",
                    backgroundColor: this.dark ? "rgba(17, 24, 39, 0.95)" : "rgba(255, 255, 255, 0.97)",
                    borderColor: gridColor,
                    style: { color: this.dark ? "#f3f4f6" : "#111827" },
                },
                series: [
                    {
                        type: "column",
                        name: "Tentativas",
                        yAxis: 1,
                        color: this.dark ? "#4b5563" : "#d1d5db",
                        data: daily.map((day) => [Date.parse(`${day.date}T00:00:00`), day.attempts]),
                    },
                    {
                        type: "line",
                        name: "Taxa de falha (%)",
                        color: "#ef4444",
                        lineWidth: 3,
                        marker: { enabled: true, radius: 3 },
                        tooltip: { valueSuffix: "%" },
                        data: daily.map((day) => [Date.parse(`${day.date}T00:00:00`), Math.round(day.failure_rate * 1000) / 10]),
                    },
                ],
            };
        },
    },

    watch: {
        producer() {
            this.search();
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        search() {
            if (this.webhookIdSearch) {
                this.openDetailFromSearch();

                return;
            }

            this.pagination.current_page = 1;
            this.fetch();
        },

        openDetailFromSearch() {
            const webhookId = parseInt(this.webhookIdSearch, 10);

            if (webhookId > 0) {
                this.openDetail(webhookId);
            }
        },

        fetch() {
            this.loading = true;

            const params = {
                page: this.pagination.current_page,
                per_page: this.pagination.per_page,
                status: this.filters.status || undefined,
                user_id: this.producer || undefined,
                min_rate: this.filters.min_rate !== "" ? this.filters.min_rate : undefined,
                no_success_days: this.filters.no_success_days !== "" ? this.filters.no_success_days : undefined,
                only_429: this.filters.only_429 ? 1 : undefined,
            };

            axios
                .get("/api/webhook/analytics/ranking", { params })
                .then(({ data }) => {
                    this.cards = data.cards;
                    this.webhooks = data.data;
                    this.pagination = {
                        current_page: data.current_page,
                        per_page: data.per_page,
                        total: data.total,
                        from: data.from ?? 0,
                        to: data.to ?? 0,
                        last_page: data.last_page,
                    };
                })
                .catch(({ response }) => {
                    this.toast.error(response?.data?.message || "Falha ao carregar o ranking");
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        openDetail(webhookId) {
            this.detailId = webhookId;
            this.detail = null;
            this.loadingDetail = true;

            axios
                .get(`/api/webhook/analytics/${webhookId}`)
                .then(({ data }) => {
                    this.detail = data;
                })
                .catch(({ response }) => {
                    this.detailId = null;
                    this.toast.error(response?.data?.message || `Webhook ${webhookId} não encontrado`);
                })
                .finally(() => {
                    this.loadingDetail = false;
                });
        },

        closeDetail() {
            this.detailId = null;
            this.detail = null;
        },

        percent(rate) {
            return `${Math.round((rate ?? 0) * 1000) / 10}%`;
        },

        codeLabel(code, count) {
            if (!code) {
                return "-";
            }

            return `${code === "no_response" ? "sem resposta" : code} x${count}`;
        },

        codesLabel(codes) {
            const entries = Object.entries(codes ?? {}).sort((a, b) => b[1] - a[1]);

            if (entries.length === 0) {
                return "-";
            }

            return entries
                .slice(0, 2)
                .map(([code, count]) => this.codeLabel(code, count))
                .join(", ");
        },

        rateClass(rate) {
            if (rate >= 1) {
                return "font-semibold text-red-600 dark:text-red-400";
            }

            if (rate >= 0.5) {
                return "font-semibold text-amber-600 dark:text-amber-400";
            }

            return "text-gray-700 dark:text-gray-200";
        },

        statusBadgeClass(status) {
            const base = "inline-flex rounded-full px-2 py-0.5 text-xs font-medium ";
            const byStatus = {
                enabled: "bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300",
                disabled: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300",
                deactivated: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300",
            };

            return base + (byStatus[status] || "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300");
        },

        attemptStatusClass(status) {
            const base = "inline-flex rounded px-2 py-0.5 text-xs font-medium ";

            if (status === "success") {
                return base + "bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300";
            }

            if (status === "failed") {
                return base + "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300";
            }

            return base + "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300";
        },
    },
};
</script>
